/**
 * Address value type and the pure helpers that operate on it.
 *
 * Lifted verbatim out of the legacy `src/components/AddressLookup.tsx` when that
 * component was retired on 2026-09-26. They never belonged to the picker — every
 * one of them is a pure function about an address, and they had accumulated ten
 * importers across pages, utilities and the v2 picker itself, which is why the
 * legacy file could not be deleted for four months after it stopped being rendered.
 *
 * Nothing here talks to Postcoder. The network side lives in `postcoder.ts`.
 */
import React from 'react';

export interface Address {
  line1?: string;
  line2?: string;
  city?: string;
  county?: string;
  postCode?: string;
  country?: string;
  state?: string;
}

export function simplifyAddress(address: Address): string {
  if (
    !address.line1 &&
    !address.line2 &&
    !address.city &&
    !address.county &&
    !address.postCode &&
    !address.country
  ) {
    return '';
  }

  let addressString = address.line1 + ', ';

  if (address.line2) {
    addressString += address.line2 + ', ';
  }

  addressString += (address.city ?? '') + ', ';
  addressString += (address.county ?? '') + ', ';
  addressString += (address.postCode ?? '') + ', ';
  addressString += address.country ?? '';

  return addressString;
}

export function validateAddress(address: Address) {
  const regex = new RegExp(
    '^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$',
  );

  return (
    address.postCode &&
    regex.test(address.postCode) &&
    address.line1 &&
    address.postCode
  );
}

export function testPostCode(address: Address): JSX.Element | null {
  const regex = new RegExp(
    '^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$',
  );

  // Only show error message if postcode is invalid
  if (address.postCode && !regex.test(address.postCode)) {
    return (
      <div style={{
        color: '#ff5757',
        backgroundColor: '#ffe5e5',
        padding: '12px 16px',
        borderRadius: '8px',
        marginTop: '12px',
        fontSize: '14px',
        fontWeight: '500',
        border: '1px solid #ffcccc'
      }}>
        Please ensure a valid postal code
      </div>
    );
  }
  
  // Return null when valid or empty (no error to show)
  return null;
}

// Function to determine city from UK postcode
export function getCityFromPostcode(postcode: string): string | null {
  if (!postcode) return null;

  const postcodeUpper = postcode.toUpperCase().trim();

  // Extract the postcode area (first 1-2 letters before the number)
  const postcodeArea = postcodeUpper.match(/^([A-Z]{1,2})/)?.[1];
  // Extract the postcode district (area + first digits, e.g. CM7)
  const postcodeDistrict = postcodeUpper.match(/^([A-Z]{1,2}\d{1,2})/)?.[1];

  if (!postcodeArea) return null;

  // Check specific districts first (more specific match takes priority)
  const districtToCityMap: Record<string, string> = {
    'CM7': 'Essex',
  };

  if (postcodeDistrict && districtToCityMap[postcodeDistrict]) {
    return districtToCityMap[postcodeDistrict];
  }

  // London postcode areas
  const londonAreas = [
    'E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC',
    'BR', 'CR', 'DA', 'EN', 'HA', 'IG', 'KT', 'RM', 'SM', 'TW', 'UB'
  ];

  // Exeter postcode areas
  const exeterAreas = ['EX'];

  // Sheffield postcode areas
  const sheffieldAreas = ['S'];

  // Cambridge postcode areas
  const cambridgeAreas = ['CB'];

  if (londonAreas.includes(postcodeArea)) {
    return 'London';
  } else if (exeterAreas.includes(postcodeArea)) {
    return 'Exeter';
  } else if (sheffieldAreas.includes(postcodeArea)) {
    return 'Sheffield';
  } else if (cambridgeAreas.includes(postcodeArea)) {
    return 'Cambridge';
  }

  return null;
}

// Function to check if postcode is in allowed cities
export function isPostcodeInAllowedCities(
  postcode: string,
  allowedCities: string[]
): boolean {
  const city = getCityFromPostcode(postcode);
  return city !== null && allowedCities.includes(city);
}
